import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Plus, Search, Trash2, Package, Tag, DollarSign, Globe,
    Image as ImageIcon, GitBranch, CheckCircle2, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
    { value: 'workshops', label: 'Workshops' },
    { value: 'ai_consulting', label: 'AI Consulting' },
    { value: 'software', label: 'Software' },
    { value: 'certifications', label: 'Certifications' },
    { value: 'embedded_teams', label: 'Embedded Teams' },
];

const STATUSES = [
    { value: 'active', label: 'Active', color: 'bg-emerald-50 text-emerald-700' },
    { value: 'coming_soon', label: 'Coming Soon', color: 'bg-amber-50 text-amber-700' },
    { value: 'archived', label: 'Archived', color: 'bg-stone-100 text-stone-500' },
];

const DEFAULT_PRICING_MODELS = [
    { value: 'per_participant', label: 'Per Participant' },
    { value: 'flat_fee', label: 'Flat Fee' },
    { value: 'per_event', label: 'Per Event' },
];

const CURRENCIES = [
    { value: 'USD', symbol: '$' },
    { value: 'EUR', symbol: '\u20ac' },
    { value: 'GBP', symbol: '\u00a3' },
    { value: 'AUD', symbol: 'A$' },
];

const currencySymbol = (code) => CURRENCIES.find(c => c.value === code)?.symbol || '$';

export default function Products() {
    const { getAuthHeader } = useAuth();
    const { can } = usePermissions();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('all');
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [pricingModels, setPricingModels] = useState(DEFAULT_PRICING_MODELS);

    useEffect(() => {
        loadProducts();
        // Load org-specific product settings
        (async () => {
            try {
                const res = await axios.get(`${API}/products/settings`, { headers: getAuthHeader() });
                if (res.data.categories?.length) setCategories(res.data.categories);
                if (res.data.pricing_models?.length) setPricingModels(res.data.pricing_models);
            } catch {}
        })();
    }, []);

    const loadProducts = async () => {
        try {
            const res = await axios.get(`${API}/products`, { headers: getAuthHeader() });
            setProducts(res.data);
        } catch { toast.error('Failed to load products'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (e, productId) => {
        e.stopPropagation();
        if (!window.confirm('Delete this product? This will also remove all version history and media.')) return;
        try {
            await axios.delete(`${API}/products/${productId}`, { headers: getAuthHeader() });
            toast.success('Product deleted');
            loadProducts();
        } catch { toast.error('Failed to delete product'); }
    };

    const filtered = products.filter(p => {
        if (catFilter !== 'all' && p.category !== catFilter) return false;
        if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.sku.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const getCategoryLabel = (val) => categories.find(c => c.value === val)?.label || val;
    const getStatusStyle = (val) => STATUSES.find(s => s.value === val) || STATUSES[0];
    const getPricingLabel = (val) => pricingModels.find(m => m.value === val)?.label || val;

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="products-title">Products</h1>
                    <p className="text-sm text-stone-500 mt-1">Manage your product catalog, content, and pricing</p>
                </div>
                <Button onClick={() => navigate('/products/new')} className="bg-stone-900 hover:bg-stone-800" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Product
                </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="pl-9" data-testid="product-search" />
                </div>
                <div className="flex gap-1.5">
                    {[{ value: 'all', label: 'All' }, ...categories].map(cat => (
                        <button key={cat.value} onClick={() => setCatFilter(cat.value)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${catFilter === cat.value ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                            data-testid={`filter-${cat.value}`}>
                            {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Products Grid */}
            {filtered.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-stone-200 rounded-xl">
                    <Package className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No products found</h3>
                    <p className="text-stone-500 mb-4">{products.length === 0 ? 'Create your first product to get started' : 'Try adjusting your filters'}</p>
                    {products.length === 0 && <Button onClick={() => navigate('/products/new')}>Add First Product</Button>}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(product => {
                        const statusStyle = getStatusStyle(product.status);
                        const hasMedia = product.media?.length > 0 || product.featured_image_url;
                        const isPublished = !!product.published_at;

                        return (
                            <div
                                key={product.product_id}
                                onClick={() => navigate(`/products/${product.product_id}`)}
                                className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-md hover:border-stone-300 transition-all cursor-pointer group"
                                data-testid={`product-card-${product.product_id}`}
                            >
                                {/* Featured image or placeholder */}
                                {product.featured_image_resolved_url ? (
                                    <div className="h-36 bg-stone-100 overflow-hidden">
                                        <img src={product.featured_image_resolved_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                    </div>
                                ) : (
                                    <div className="h-20 bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center">
                                        <Package className="w-8 h-8 text-stone-200" />
                                    </div>
                                )}

                                <div className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-stone-900 truncate group-hover:text-stone-700 transition-colors">{product.name}</h3>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-xs text-stone-400 font-mono">{product.sku}</span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusStyle.color}`}>{statusStyle.label}</span>
                                            </div>
                                        </div>
                                        {can('products:delete') && (
                                            <button
                                                onClick={(e) => handleDelete(e, product.product_id)}
                                                className="p-1.5 hover:bg-red-50 rounded text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                                data-testid={`delete-product-${product.product_id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    {product.description && (
                                        <p className="text-xs text-stone-500 mb-3 line-clamp-2">{product.description}</p>
                                    )}

                                    <div className="flex items-center gap-2 text-[10px] text-stone-400 mb-3 flex-wrap">
                                        <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{getCategoryLabel(product.category)}</span>
                                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{getPricingLabel(product.pricing_model)}</span>
                                        {product.version > 1 && (
                                            <span className="flex items-center gap-1"><GitBranch className="w-3 h-3" />v{product.version}</span>
                                        )}
                                        {hasMedia && (
                                            <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" />{product.media?.length || 0}</span>
                                        )}
                                    </div>

                                    <div className="pt-3 border-t border-stone-100 flex items-center justify-between">
                                        <span className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            {currencySymbol(product.currency)}{(product.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {product.region_pricing?.length > 0 && (
                                                <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                                    <Globe className="w-2.5 h-2.5" />{product.region_pricing.length}
                                                </span>
                                            )}
                                            {isPublished ? (
                                                <span className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                                                    <CheckCircle2 className="w-2.5 h-2.5" />Published
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                                    <Clock className="w-2.5 h-2.5" />Draft
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
